/**
 * Decision 0155: when a rate-limited run may run again, and which limit ran out. Pure; the hub fills a `rate_limited`
 * run's end with it (`RunEnded.rateLimit`), core continues a worker at the reset, the lead's body retries a turn then,
 * and the turn file shows quotas that are near their limit. Facts only: what to do about a near limit is the lead's
 * judgement.
 */
import type { QuotaWindow as QuotaWindowSchema, RateLimitReset } from "@minga/protocol"

type QuotaWindow = typeof QuotaWindowSchema.Type

type Window = NonNullable<RateLimitReset["window"]>

/** A rate-limited worker or turn starts again this long after its limit's reset (the provider's clock may lag). */
export const RATE_LIMIT_RESUME_SLACK_MS = 60_000

/** The turn file's quota insight: a window at or above this share is near its limit. */
export const QUOTA_NEAR_PERCENT = 80

/** A limit's name in words: "the 5-hour limit", "the weekly limit". */
export const limitWindowWords = (window: Window | undefined): string =>
  window === "5h"
    ? "5-hour limit"
    : window === "7d"
      ? "weekly limit"
      : window === "24h"
        ? "daily limit"
        : "usage limit"

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"]

/** The offset of `zone` at `at` (ms, local = UTC + offset); null for a zone this runtime does not know. */
const zoneOffset = (at: number, zone: string): number | null => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).formatToParts(new Date(at))
    const n = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? Number.NaN)
    const local = Date.UTC(n("year"), n("month") - 1, n("day"), n("hour") % 24, n("minute"), n("second"))
    return Number.isFinite(local) ? local - Math.floor(at / 1000) * 1000 : null
  } catch {
    return null
  }
}

/** The UTC instant of a wall-clock time in `zone` (two passes cover a DST change between the guess and the answer). */
const zoned = (y: number, mo: number, d: number, h: number, mi: number, zone: string): number | null => {
  const guess = Date.UTC(y, mo, d, h, mi)
  const first = zoneOffset(guess, zone)
  if (first === null) return null
  const second = zoneOffset(guess - first, zone)
  return second === null ? null : guess - second
}

const windowOfText = (t: string): Window | undefined =>
  /\b(?:5|five)[- ]?h(?:ou)?r|\bsession limit/i.test(t)
    ? "5h"
    : /\bweekly|\b7[- ]?day|\bseven[- ]day/i.test(t)
      ? "7d"
      : /\bdaily|\b24[- ]?h/i.test(t)
        ? "24h"
        : undefined

/**
 * The reset a provider's limit message names, after `now`:
 * - Claude Code: "You've hit your limit · resets 9:10pm (UTC)", "5-hour limit reached ∙ resets 3am (Europe/Berlin)",
 *   "Weekly limit reached · resets Oct 9, 5pm", and the older "Claude AI usage limit reached|1730000000";
 * - Codex: "Try again in 2 hours 13 minutes", "try again at 3:45 PM".
 * A clock time without a date is the next one after `now` (a minute of slack for a message read late); a zone the
 * runtime does not know, or none, is UTC (the sandbox's clock). Null when the text names no reset.
 */
export const rateLimitResetFromText = (
  text: string | null | undefined,
  now: number,
): RateLimitReset | null => {
  const t = text ?? ""
  if (!t) return null
  const window = windowOfText(t)
  const out = (resetsAt: number): RateLimitReset => ({ resetsAt, ...(window ? { window } : {}) })
  const epoch = /limit reached\|(\d{10,13})\b/i.exec(t)
  if (epoch?.[1]) {
    const n = Number(epoch[1])
    return out(epoch[1].length <= 10 ? n * 1000 : n)
  }
  const rel =
    /try again in\s+(?:(\d+)\s*(?:days?|d)\b)?\s*(?:(\d+)\s*(?:hours?|hrs?|h)\b)?\s*(?:(\d+)\s*(?:minutes?|mins?|m)\b)?/i.exec(
      t,
    )
  if (rel && (rel[1] || rel[2] || rel[3]))
    return out(now + ((Number(rel[1] ?? 0) * 24 + Number(rel[2] ?? 0)) * 60 + Number(rel[3] ?? 0)) * 60_000)
  const at =
    /(?:resets|try again at)\s+(?:at\s+)?(?:([A-Za-z]{3})[a-z]*\.?\s+(\d{1,2}),?\s+(?:at\s+)?)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s*(?:\(([^)]+)\)|(UTC|GMT)\b)?/i.exec(
      t,
    )
  if (!at?.[3]) return null
  let hour = Number(at[3])
  const minute = Number(at[4] ?? 0)
  const ampm = at[5]?.toLowerCase()
  if (ampm === "pm" && hour < 12) hour += 12
  if (ampm === "am" && hour === 12) hour = 0
  if (hour > 23 || minute > 59) return null
  const rawZone = (at[6] ?? at[7] ?? "UTC").trim()
  const zone = /^(?:utc|gmt|z)$/i.test(rawZone) ? "UTC" : rawZone
  const instant = (y: number, mo: number, d: number) =>
    zoned(y, mo, d, hour, minute, zone) ?? Date.UTC(y, mo, d, hour, minute)
  const today = new Date(now)
  const month = at[1] ? MONTHS.indexOf(at[1].toLowerCase()) : -1
  if (month >= 0 && at[2]) {
    const day = Number(at[2])
    const y = today.getUTCFullYear()
    const thisYear = instant(y, month, day)
    return out(thisYear >= now - 86_400_000 ? thisYear : instant(y + 1, month, day))
  }
  // the next such clock time: today in the zone, else tomorrow
  for (let d = -1; d <= 2; d++) {
    const day = new Date(now + d * 86_400_000)
    const candidate = instant(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate())
    if (candidate > now - 60_000) return out(candidate)
  }
  return null
}

/**
 * The reset of a profile's quota that ran out: the windows at 100 % with a future reset (the latest of them: every one
 * must have reset). Without one, the fullest window at 90 % or more (a CLI's last report before the refusal may read
 * 99 %). Null when no window explains the limit.
 */
export const exhaustedQuotaReset = (
  quota: ReadonlyArray<QuotaWindow>,
  now: number,
): RateLimitReset | null => {
  const future = quota.filter(
    (q): q is QuotaWindow & { resetsAt: number } => q.resetsAt !== null && q.resetsAt > now,
  )
  const full = future.filter((q) => q.usedPercent >= 100)
  const pick = full.length
    ? full.reduce((a, b) => (b.resetsAt > a.resetsAt ? b : a))
    : future
        .filter((q) => q.usedPercent >= 90)
        .reduce<(QuotaWindow & { resetsAt: number }) | null>(
          (a, b) =>
            a === null ||
            b.usedPercent > a.usedPercent ||
            (b.usedPercent === a.usedPercent && b.resetsAt < a.resetsAt)
              ? b
              : a,
          null,
        )
  if (!pick) return null
  return { resetsAt: pick.resetsAt, ...(pick.window !== "other" ? { window: pick.window } : {}) }
}

/**
 * Decision 0155: when a `rate_limited` run may run again. The provider's own words first (they name the limit that
 * refused it), then the profile's quota (a sandbox's last report may still be on its way). Null when neither says.
 */
export const rateLimitResetOf = (o: {
  readonly errorMessage: string | null | undefined
  readonly quota: ReadonlyArray<QuotaWindow>
  readonly readinessResetsAt?: number | null
  readonly now: number
}): RateLimitReset | null => {
  const fromText = rateLimitResetFromText(o.errorMessage, o.now)
  if (fromText && fromText.resetsAt > o.now - 60_000) {
    if (fromText.window) return fromText
    const fromQuota = exhaustedQuotaReset(o.quota, o.now)
    return fromQuota?.window && Math.abs(fromQuota.resetsAt - fromText.resetsAt) < 5 * 60_000
      ? { ...fromText, window: fromQuota.window }
      : fromText
  }
  const fromQuota = exhaustedQuotaReset(o.quota, o.now)
  if (fromQuota) return fromQuota
  return o.readinessResetsAt != null && o.readinessResetsAt > o.now ? { resetsAt: o.readinessResetsAt } : null
}

/**
 * Decision 0155 ("refresh when read"): a quota as it stands now. A window whose reset has passed says nothing about
 * now (the 10-23 review read "99 %, 5h" sixteen hours after its reset): it is left out until the next observation, as
 * placement already ignores it. Unknown is unknown capacity, never 0 %.
 */
export const currentQuota = <Q extends QuotaWindow>(quota: ReadonlyArray<Q>, now: number): Array<Q> =>
  quota.filter((q) => q.resetsAt === null || q.resetsAt > now)

/** Decision 0155: the windows of a quota at or above `QUOTA_NEAR_PERCENT` with a future reset, fullest first. */
export const nearLimitWindows = <Q extends QuotaWindow>(quota: ReadonlyArray<Q>, now: number): Array<Q> =>
  quota
    .filter((q) => q.usedPercent >= QUOTA_NEAR_PERCENT && q.resetsAt !== null && q.resetsAt > now)
    .sort((a, b) => b.usedPercent - a.usedPercent)
